import classnames from "classnames";
import { ButtonPrimary } from "components/Atoms/Button";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import TextLinkButton from "components/buttons/TextLinkButton";
import { useCredits } from "credits/useCredits";
import { Loading, Panel } from "components/panels";
import { AutoReloadModal } from "credits/AutoReloadModalApp";
import { BuyCreditsModal } from "credits/BuyCreditsModalApp";
import { CreditApi } from "queries/Credit";
import React from "react";
import { formatCurrency } from "utilities/currencyutils";

type PanelType = "s1_panel" | "s1_related-feature-panel";

interface Props {
  panelType: PanelType;
  style?: React.CSSProperties;
}

export const CreditBalancePanel = (props: Props) => {
  const isPaid = site.currentEnvironment.client.access === "activePaid";
  const credits = useCredits();
  const creditAccountQuery = CreditApi.GetCreditAccountByClient.useQuery(
    { clientId: site.currentEnvironment.client.clientId },
    { enabled: isPaid }
  );
  const [showAutoReloadModal, setShowAutoReloadModal] = React.useState(false);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = React.useState(false);

  const account = creditAccountQuery.data?.data;
  const isLowBalance = credits != null
    && credits < serverReference.constants.credit.lowBalanceWarningThreshold;

  return <>
    <Panel
      panelClassName={props.panelType}
      title={RESX.Credit.CreditBalance}
      subTitle="Your credit balance will be consumed with AI data import and Peppol usage."
      style={props.style}
    >
      <div className="credit-balance-panel">
        <div className={classnames("credit-balance-panel-card balance-card", { "low-balance": isLowBalance })}>
          {credits == null
            ? <Loading />
            : <h2>{formatCurrency(credits, "eur", { hideCents: credits > 1000 })}</h2>}
          <div>{isLowBalance ? "Remaining balance is low" : RESX.Credit.RemainingBalance}</div>
        </div>

        {isPaid
          ? <div className="credit-balance-panel-right">
            <div className="credit-balance-panel-card auto-reload-card">
              <div className="auto-reload-checkbox">
                {account == null
                  ? <Loading />
                  : <Checkbox
                    checked={account.autoReloadEnabled}
                    onChange={() => setShowAutoReloadModal(true)}
                  />}
              </div>
              <div className="grow1 flex-column gap-small">
                {account == null
                  ? <Loading />
                  : <>
                    <div>
                      {account.autoReloadEnabled
                        ? <>Auto reload is <strong>enabled</strong>. We will reload to <strong>{formatCurrency(account.autoReloadGoalAmount, "eur", {hideCents: true})}</strong> when the balance drops below <strong>{formatCurrency(account.autoReloadTriggerAmount, "eur", {hideCents: true})}</strong>.</>
                        : <>Auto reload is <strong>disabled</strong>. Enable auto reload to avoid interruptions when credits are fully spent. </>}
                    </div>
                    <div>
                      <TextLinkButton
                        onClick={() => setShowAutoReloadModal(true)}
                        style={{ padding: 0 }}
                      >
                        Configure auto reload
                      </TextLinkButton>
                    </div>
                  </>}
              </div>
            </div>

            <div className="buy-credits-wrapper">
              <ButtonPrimary label={RESX.Credit.BuyCredits} onClick={() => setShowBuyCreditsModal(true)} />
            </div>
          </div>
          : <div className="credit-balance-panel-card" style={{ alignSelf: "flex-start" }}>
            Need more credits? Contact support.
          </div>}
      </div>
    </Panel>

    {isPaid && showAutoReloadModal && <AutoReloadModal onClose={() => setShowAutoReloadModal(false)} />}
    {isPaid && showBuyCreditsModal && <BuyCreditsModal onClose={() => setShowBuyCreditsModal(false)} />}
  </>;
};
